"use client";

import React from "react";
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { Form, FormField, FormItem, FormLabel, FormControl, FormMessage, FormDescription } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { CheckCircle2, Send, AlertCircle, Loader2 } from "lucide-react";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
function TipFormInner() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    resetForm,
    control
  } = useWvcForm();
  if (isSubmitted && !submitError) {
    return <div data-section-id="118493" className="p-10 bg-primary/10 border-2 border-primary rounded-2xl text-center space-y-6 animate-in fade-in zoom-in duration-500">
        <div className="mx-auto size-20 bg-primary/20 rounded-full flex items-center justify-center mb-6">
          <CheckCircle2 className="size-10 text-primary" />
        </div>
        <h3 className="text-4xl font-black tracking-tighter text-foreground uppercase">Lead Secured</h3>
        <p className="text-xl text-muted-foreground max-w-md mx-auto">{successMessage}</p>
        <Button onClick={resetForm} className="mt-8 bg-primary text-primary-foreground hover:bg-primary/90 h-14 px-8 text-lg font-bold uppercase tracking-wide shadow-[0_0_20px_rgba(220,38,38,0.4)] transition-all hover:scale-105">
          Submit Another Lead
        </Button>
      </div>;
  }
  return <Form onSubmit={handleSubmit} className="space-y-8">
      <div className="space-y-6 bg-card/50 backdrop-blur-xl p-8 md:p-12 rounded-3xl border border-border shadow-2xl">
        
        <FormField control={control} name="name" rules={{
        required: "We need a name for our records."
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="text-base font-bold uppercase tracking-wider text-foreground/80">Your Name</FormLabel>
              <FormControl>
                <Input placeholder="John Smith" className="text-lg h-16 px-6 bg-background/50 border-2 border-input focus-visible:border-primary focus-visible:ring-primary/50 focus-visible:ring-4 transition-all rounded-xl" {...field} />
              </FormControl>
              <FormDescription className="text-sm">How should we address you in correspondence?</FormDescription>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="email" rules={{
        required: "Email is required for confidential follow-ups.",
        pattern: {
          value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
          message: "Please enter a valid email address."
        }
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="text-base font-bold uppercase tracking-wider text-foreground/80">Secure Email</FormLabel>
              <FormControl>
                <Input type="email" placeholder="jsmith@protonmail.com" className="text-lg h-16 px-6 bg-background/50 border-2 border-input focus-visible:border-primary focus-visible:ring-primary/50 focus-visible:ring-4 transition-all rounded-xl" {...field} />
              </FormControl>
              <FormDescription className="text-sm">We may contact you for further documentation.</FormDescription>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="subject" render={({
        field
      }) => <FormItem>
              <FormLabel className="text-base font-bold uppercase tracking-wider text-foreground/80">Investigation Subject</FormLabel>
              <FormControl>
                <Input placeholder="e.g., Real estate tax avoidance" className="text-lg h-16 px-6 bg-background/50 border-2 border-input focus-visible:border-primary focus-visible:ring-primary/50 focus-visible:ring-4 transition-all rounded-xl" {...field} />
              </FormControl>
              <FormDescription className="text-sm">A brief title for your disclosure.</FormDescription>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="message" rules={{
        required: "Please provide details regarding your lead."
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="text-base font-bold uppercase tracking-wider text-foreground/80">Details & Evidence</FormLabel>
              <FormControl>
                <Textarea placeholder="Describe the financial or legal mechanisms in detail..." className="text-lg min-h-[200px] p-6 bg-background/50 border-2 border-input focus-visible:border-primary focus-visible:ring-primary/50 focus-visible:ring-4 transition-all rounded-xl resize-y" {...field} />
              </FormControl>
              <FormDescription className="text-sm">Include dates, names, and specific entities if possible.</FormDescription>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="agree" rules={{
        required: "You must confirm the veracity of this report."
      }} render={({
        field
      }) => <FormItem className="flex flex-row items-start space-x-4 space-y-0 p-6 bg-muted/30 rounded-xl border border-border/50">
              <FormControl>
                <input type="checkbox" className="mt-1 size-6 shrink-0 rounded-md border-2 border-input bg-background accent-primary focus-visible:outline-none focus-visible:ring-4 focus-visible:ring-primary/50 transition-all cursor-pointer" checked={field.value} onChange={field.onChange} onBlur={field.onBlur} ref={field.ref} />
              </FormControl>
              <div className="space-y-1 leading-none">
                <FormLabel className="text-base font-medium cursor-pointer">
                  I confirm this information is accurate to the best of my knowledge.
                </FormLabel>
                <FormDescription className="text-sm">
                  Verification is required for investigative submission.
                </FormDescription>
              </div>
            </FormItem>} />

        {submitError && <div className="p-4 bg-destructive/10 border border-destructive/20 rounded-xl flex items-start gap-3 text-destructive">
            <AlertCircle className="size-5 mt-0.5 shrink-0" />
            <p className="text-sm font-medium">{submitError}</p>
          </div>}

        <Button type="submit" disabled={isSubmitting} className="w-full h-16 text-xl font-black uppercase tracking-widest bg-gradient-to-b from-primary/90 to-primary hover:from-primary hover:to-primary/90 text-primary-foreground shadow-[inset_0_2px_0_rgba(255,255,255,0.2),0_10px_20px_-10px_rgba(0,0,0,0.5)] transition-all hover:scale-[1.02] active:scale-[0.98] rounded-xl group">
          {isSubmitting ? <>
              <Loader2 className="animate-spin mr-3 size-6" />
              Transmitting...
            </> : <>
              Submit Lead
              <Send className="ml-3 size-6 group-hover:translate-x-1 group-hover:-translate-y-1 transition-transform" />
            </>}
        </Button>
      </div>
    </Form>;
}
export default function DropATip() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  return;
}